import {
  useGetCombinedGames,
  useInfiniteScroll,
  useRecentSearches,
  useSearch
} from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { MIN_SEARCH_VALUE_LENGTH, MODAL_SEARCH } from '../../constants';
import { InputSearch } from 'src/components/Inputs/InputSearch/InputSearch';
import { GamesGrid } from 'src/components/Games/GamesGrid';
import { RecentSearches } from '../RecentSearches';
import { NoGamesStub } from '../NoGamesStub';
import noGames from 'src/assets/images/noSearchGames.webp';
import { useModalContext } from 'src/hooks/useModalContext';

export const SearchingGames = () => {
  const { recentSearches, saveRecentResult, deleteRecentSearch } = useRecentSearches({
    storageKey: 'recentSearches',
    maxStoredSearchesLength: 6
  });

  const { closeModal } = useModalContext();
  const { value, onChange, debouncedValue } = useSearch();

  const { t } = useTranslation();

  const enableRequest = Boolean(debouncedValue && debouncedValue.length >= MIN_SEARCH_VALUE_LENGTH);

  const {
    games: stubGames,
    fetchNextPage: fetchNextPageStubGames,
    hasNextPage: hasNextPageStubGames,
    isFetching: isFetchingStubGames,
    isFetched: isFetchedStubGames
  } = useGetCombinedGames(
    { per_page: 9 },
    { categoryId: 'top', target: 'topbarSearchRecommended' }
  );

  const {
    allGames: games,
    fetchNextPage,
    hasNextPage,
    isFetched,
    isFetching
  } = useGetCombinedGames(
    { name: debouncedValue, per_page: 12 },
    { enabled: enableRequest, target: 'topbarSearch' }
  );

  const handleSaveRecentResult = () => {
    closeModal();
    sessionStorage.setItem(MODAL_SEARCH, 'true');
    if (games.length) {
      saveRecentResult(value, true);
    }
  };

  const { parentRef, childRef } = useInfiniteScroll({
    callback: () => {
      if (hasNextPage && value.length >= MIN_SEARCH_VALUE_LENGTH) {
        fetchNextPage();
      }
    }
  });

  const { parentRef: parentRefStubGames, childRef: childRefStubGames } = useInfiniteScroll({
    callback: () => {
      if (hasNextPageStubGames) {
        fetchNextPageStubGames();
      }
    }
  });

  const handleRecentSearchClick = (item: string) => {
    onChange(item);
  };

  const gamesFound = games.length > 0;
  const gamesNotFound = games.length === 0 && value.length !== 0 && isFetched;

  return (
    <div className="px-1 flex flex-col">
      <InputSearch
        placeholder={'general.searchGamePlaceholder'}
        value={value}
        onChange={onChange}
      />
      {!value && Boolean(recentSearches?.length) && (
        <RecentSearches
          items={recentSearches}
          handleDelete={deleteRecentSearch}
          handleClick={handleRecentSearchClick}
        />
      )}

      {gamesNotFound && (
        <NoGamesStub image={noGames}>{t('gamesPage.noGamesInCategory')}</NoGamesStub>
      )}

      {gamesFound && (
        <div ref={parentRef}>
          <h3 className="text-sm_search_result_text font-bold text-base mt-3">
            {t('general.searchResults')}
          </h3>
          <div className="overflow-auto mt-3">
            <GamesGrid
              isFetching={isFetching}
              isFetched={isFetched}
              className="pb-10"
              games={games}
              gameCardProps={{ onClick: handleSaveRecentResult }}
            />
            {!isFetching && hasNextPage && (
              <div className="h-5 mb-[70px] w-full" ref={childRef}></div>
            )}
          </div>
        </div>
      )}
      <p className="text-sm_searching_games_top_text font-bold text-base mt-3">
        {t('gameCategories.top')} {t('general.games')}
      </p>

      {stubGames && (
        <div className="overflow-auto mt-2.5" ref={parentRefStubGames}>
          <GamesGrid
            gameCardProps={{ onClick: handleSaveRecentResult }}
            isFetching={isFetchingStubGames}
            isFetched={isFetchedStubGames}
            games={stubGames}
            className="mb-[110px]"
          />
          {!isFetchingStubGames && hasNextPageStubGames && (
            <div className="h-[20px] w-full" ref={childRefStubGames} />
          )}
        </div>
      )}
    </div>
  );
};
